import { isChildNode } from 'module/helpers/is-child-node';
import { useTranslation } from 'module/hooks/use-translation';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { Popover } from 'module/ui/components/atoms/popover';
import { OptionTypeBase, SelectProps } from 'module/ui/components/atoms/select';
import { ChevronDown } from '@carbon/icons-react';
import { PopoverProps } from 'antd';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';
import { GroupedDropdownTrigger } from './components';
import { Content } from './content';

export const SingleLineEllipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface GroupedDropdownTriggerProps {
  value?: OptionTypeBase;
  onClick: () => void;
}

interface GroupedDropdownProps<
  OptionType extends OptionTypeBase = OptionTypeBase,
> {
  title?: string | React.ReactNode;
  triggerComponent?: React.ComponentType<GroupedDropdownTriggerProps>;
  selectProps?: Omit<SelectProps<OptionType>, 'onChange' | 'value' | 'options'>;
  options: SelectProps<OptionType>['options'];
  onChange: SelectProps<OptionType>['onChange'];
  value?: OptionTypeBase;
  popoverProps?: Partial<PopoverProps>;
  customComponentClass?: string;
}

const GROUPED_DROPDOWN_HOSTED_COMPONENT_CLASS =
  'grouped-dropdown-hosted-component';

const StyledPopover = styled(Popover)`
  &.ant-popover {
    width: 296px;
    height: 448px;
  }
  .ant-popover-inner,
  .ant-popover-inner-content {
    height: 100%;
    width: 100%;
  }
  .ant-popover-inner-content,
  .ozone-select-container {
    display: flex;
    flex-flow: column;
  }
  .ozone-select-container {
    flex: 1;
    overflow: hidden;
  }
`;

export const TextWithEllipsis = styled.span`
  ${SingleLineEllipsis};
  max-width: 190px;
`;

const DefaultTrigger = ({ value, onClick }: GroupedDropdownTriggerProps) => {
  const defaultFallback = useTranslation('select_with_ellipsis');

  return (
    <GroupedDropdownTrigger data-cy="select.fields" onClick={onClick}>
      <TextWithEllipsis>{value?.label ?? defaultFallback}</TextWithEllipsis>
      <ChevronDown />
    </GroupedDropdownTrigger>
  );
};

export const GroupedDropdown = <T extends OptionTypeBase>({
  ...props
}: GroupedDropdownProps<T>): React.JSX.Element => {
  const {
    title,
    triggerComponent: Trigger = DefaultTrigger,
    selectProps,
    value,
    onChange,
    options,
    popoverProps,
    customComponentClass,
  } = props;
  const [visible, setVisible] = useState(false);

  const GROUPED_DROPDOWN_CLASSES = useMemo(
    () => [GROUPED_DROPDOWN_HOSTED_COMPONENT_CLASS, customComponentClass ?? ''],
    [customComponentClass],
  );

  const _onChange = useCallback(
    (_value: any) => {
      onChange?.(_value);
      setVisible(false);
    },
    [onChange],
  );

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const onOutsideClick = useCallback(() => {
    if (!visible) {
      return;
    }
    close();
  }, [close, visible]);

  const isChild = isChildNode(GROUPED_DROPDOWN_CLASSES);

  return (
    <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
      <StyledPopover
        className={GROUPED_DROPDOWN_CLASSES.join(' ')}
        open={visible}
        content={
          <Content
            title={title}
            selectProps={selectProps as any}
            onChange={_onChange}
            selected={value}
            options={options}
          />
        }
        trigger="click"
        placement="bottomLeft"
        {...popoverProps}
      >
        <Trigger onClick={() => setVisible(true)} value={value} />
      </StyledPopover>
    </OutsideClickHandler>
  );
};
